import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/Functions';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Forgot password states
  const [step, setStep] = useState('login'); // 'login' | 'forgot_email' | 'forgot_otp'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ login_id: email, password }),
      });
      const res = await response.json();
      if (!response.ok) throw res;
      const { access_token, user } = res.data;

      // Restrict to admins only on this portal
      if (user.role !== 'admin') {
        setError('Access denied. This portal is for administrators only.');
        setLoading(false);
        return;
      }

      // Persist token and user info
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(user));

      navigate('/admin/dashboard');
    } catch (err) {
      setLoading(false);
      if (err.status === 'unverified') {
        setError('Your account is not verified. Please check your email for the verification OTP.');
      } else if (err.status === 'inactive') {
        setError('Your account has been deactivated. Please contact support.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Unable to connect to the server. Please try again.');
      }
    }
  };

  const handleForgotEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email_or_phone: forgotEmail }),
      });
      const res = await response.json();
      if (!response.ok) throw res;

      setSuccess('A password reset OTP has been sent to your email.');
      setStep('forgot_otp');
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (newPassword !== newPasswordConfirm) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email_or_phone: forgotEmail,
          otp: forgotOtp,
          password: newPassword,
          password_confirmation: newPasswordConfirm
        }),
      });
      const res = await response.json();
      if (!response.ok) throw res;

      setSuccess('Your password has been successfully reset. You can now login.');
      setStep('login');
      setPassword(''); // clear old password
      setEmail(forgotEmail); // pre-fill email
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        .admin-login-wrapper {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', sans-serif;
          background: #f5f7fa;
          position: relative;
          overflow: hidden;
        }

        /* Soft background shapes */
        .login-bg-shape {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(70px);
          opacity: 0.45;
          animation: shapeFloat 10s ease-in-out infinite alternate;
        }

        .login-bg-shape-1 {
          width: 520px;
          height: 520px;
          background: radial-gradient(circle, #a7f3d0, transparent 70%);
          top: -160px;
          right: -80px;
        }

        .login-bg-shape-2 {
          width: 380px;
          height: 380px;
          background: radial-gradient(circle, #c7d2fe, transparent 70%);
          bottom: -80px;
          left: -80px;
          animation-delay: -5s;
        }

        .login-bg-shape-3 {
          width: 250px;
          height: 250px;
          background: radial-gradient(circle, #bbf7d0, transparent 70%);
          top: 40%;
          left: 28%;
          animation-delay: -2s;
        }

        @keyframes shapeFloat {
          0%   { transform: translate(0, 0) scale(1); }
          100% { transform: translate(15px, -20px) scale(1.04); }
        }

        /* Subtle dot grid */
        .login-bg-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(99,102,241,0.07) 1px, transparent 1px);
          background-size: 36px 36px;
          pointer-events: none;
        }

        /* ── Left Panel ── */
        .login-left-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 48px 60px;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }

        /* Photo background */
        .login-left-panel::before {
          content: '';
          position: absolute;
          inset: 0;
          background: url('https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=1200&q=80') center/cover no-repeat;
          z-index: -2;
        }

        /* Blue and Gold brand overlay */
        .login-left-panel::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            160deg,
            rgba(6, 24, 48, 0.82) 0%,
            rgba(6, 24, 48, 0.75) 50%,
            rgba(207, 160, 71, 0.60) 100%
          );
          z-index: -1;
        }

        .login-left-content { 
          max-width: 480px;
          width: 100%;
          position: relative;
          z-index: 2;
          text-align: left;
          display: flex;
          flex-direction: column;
          height: 100%;
          justify-content: space-between;
        }

        .login-left-main {}

        .login-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 64px;
        }

        .login-brand-icon {
          width: 44px;
          height: 44px;
          background: rgba(255,255,255,0.15);
          border: 1.5px solid rgba(255,255,255,0.35);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: white;
        }

        .login-brand-text {
          font-size: 22px;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.3px;
        }

        .login-hero-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.95);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          padding: 6px 16px;
          border-radius: 50px;
          margin-bottom: 28px;
        }

        .login-hero-tag-dot {
          width: 6px;
          height: 6px;
          background: #CFA047;
          border-radius: 50%;
          box-shadow: 0 0 8px rgba(207, 160, 71,0.8);
        }

        .login-hero-title {
          font-size: 52px;
          font-weight: 800;
          color: #ffffff;
          line-height: 1.12;
          letter-spacing: -2.5px;
          margin: 0 0 22px 0;
        }

        .login-hero-title span {
          color: #CFA047;
        }

        .login-hero-desc {
          font-size: 16px;
          color: rgba(255,255,255,0.78);
          line-height: 1.75;
          margin-bottom: 52px;
          max-width: 400px;
        }

        .login-features { display: flex; flex-direction: column; gap: 14px; }

        .login-feature-item { display: flex; align-items: center; gap: 14px; }

        .login-feature-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.22);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #CFA047;
          font-size: 14px;
          flex-shrink: 0;
        }

        .login-feature-text { 
          font-size: 14px; 
          font-weight: 500;
          color: rgba(255,255,255,0.88); 
        }

        /* ── Right Panel ── */
        .login-right-panel {
          width: 520px;
          min-width: 520px;
          background: #f0fdf4;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 56px 48px;
          position: relative;
          z-index: 1;
          border: 3px solid #061830;
          border-radius: 4px;
          background: #ffffff;
        }

        /* Watermark icons */
        .login-watermark {
          position: absolute;
          color: #061830;
          opacity: 0.045;
          pointer-events: none;
          user-select: none;
          line-height: 1;
        }

        @media (max-width: 900px) {
          .login-left-panel { display: none; }
          .login-right-panel { width: 100%; min-width: 100%; }
        }

        .login-card { 
          width: 100%; 
          max-width: 400px;
        }

        .login-card-header { margin-bottom: 40px; text-align: center; }

        .login-card-title {
          font-size: 34px;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 4px 0;
          letter-spacing: -1.2px;
          line-height: 1.1;
        }

        /* Blue and Gold accent underline below title */
        .login-title-bar {
          width: 48px;
          height: 4px;
          background: linear-gradient(90deg, #061830, #CFA047);
          border-radius: 4px;
          margin: 14px auto 16px auto;
        }

        .login-card-subtitle { font-size: 14.5px; color: #64748b; line-height: 1.6; }

        /* Form */
        .admin-form-group { margin-bottom: 22px; }

        .admin-form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 700;
          font-size: 12.5px;
          color: #1e293b;
          letter-spacing: 0.4px;
          text-transform: uppercase;
        }

        .admin-input-wrap { position: relative; }

        .admin-input-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 14px;
          pointer-events: none;
          transition: color 0.2s;
        }

        .admin-input-wrap:focus-within .admin-input-icon { color: #061830; }

        .admin-form-input {
          width: 100%;
          padding: 15px 44px 15px 46px;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          font-size: 15px;
          color: #0f172a;
          font-family: 'Inter', sans-serif;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }

        .admin-form-input::placeholder { color: #cbd5e1; }

        .admin-form-input:focus {
          outline: none;
          border-color: #061830;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(6, 24, 48,0.08), 0 2px 8px rgba(0,0,0,0.06);
        }

        .admin-pass-toggle {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          font-size: 14px;
          transition: color 0.18s;
        }

        .admin-pass-toggle:hover { color: #061830; }

        .admin-form-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .admin-remember {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .admin-remember input[type="checkbox"] {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          accent-color: #061830;
          cursor: pointer;
        }

        .admin-remember-label { font-size: 13px; color: #64748b; cursor: pointer; }

        .admin-forgot {
          font-size: 13px;
          color: #061830;
          text-decoration: none;
          font-weight: 700;
          transition: color 0.18s;
        }

        .admin-forgot:hover { color: #061830; text-decoration: underline; }

        .admin-login-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #061830 0%, #061830 100%);
          border: none;
          border-radius: 50px;
          color: white;
          font-size: 15.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s;
          font-family: 'Inter', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          box-shadow: 0 10px 28px rgba(6, 24, 48,0.32);
          letter-spacing: 0.3px;
        }

        .admin-login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 16px 36px rgba(6, 24, 48,0.45);
        }

        .admin-login-btn:active:not(:disabled) { transform: scale(0.98); }
        .admin-login-btn:disabled { opacity: 0.75; cursor: not-allowed; }

        .btn-spinner {
          width: 17px;
          height: 17px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .login-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 28px 0 20px;
        }

        .login-divider-line { flex: 1; height: 1px; background: #f1f5f9; }

        .login-divider-text { font-size: 11.5px; color: #94a3b8; white-space: nowrap; font-weight: 600; letter-spacing: 0.5px; }

        .login-footer-note {
          text-align: center;
          font-size: 12px;
          color: #94a3b8;
          line-height: 1.6;
        }

        .login-footer-note a { color: #061830; text-decoration: none; font-weight: 600; }
        .login-footer-note a:hover { color: #061830; }

        .login-error-banner {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          background: #fff5f5;
          border: 1.5px solid #fca5a5;
          border-radius: 10px;
          padding: 12px 16px;
          margin-bottom: 20px;
          font-size: 13.5px;
          color: #b91c1c;
          line-height: 1.5;
          animation: errorFadeIn 0.25s ease;
        }

        .login-error-banner i { margin-top: 2px; flex-shrink: 0; }

        @keyframes errorFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="admin-login-wrapper">
        <div className="login-bg-grid"></div>
        <div className="login-bg-shape login-bg-shape-1"></div>
        <div className="login-bg-shape login-bg-shape-2"></div>
        <div className="login-bg-shape login-bg-shape-3"></div>

        {/* Left */}
        <div className="login-left-panel">
          <div className="login-left-content">

            {/* Top: Brand */}
            <div className="login-brand">
              <img src="/images/logo.png" alt="Logo" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
            </div>

            {/* Middle: Headline */}
            <div className="login-left-main">
              <div className="login-hero-tag">
                <span className="login-hero-tag-dot"></span>
                Secure Admin Portal
              </div>
              <h1 className="login-hero-title">
                Manage Your<br />
                Tax Platform<br />
                with <span>Confidence</span>
              </h1>
              <p className="login-hero-desc">
                A powerful command center to oversee users, services,
                filings, and revenue — all in one place.
              </p>
            </div>

            {/* Bottom: Features */}
            <div className="login-features">
              {[
                { icon: 'fa-shield-halved', text: 'Enterprise-grade security & role-based access' },
                { icon: 'fa-chart-line', text: 'Real-time analytics and performance insights' },
                { icon: 'fa-bolt', text: 'Instant notifications and task management' },
              ].map((f, i) => (
                <div key={i} className="login-feature-item">
                  <div className="login-feature-icon">
                    <i className={`fa-solid ${f.icon}`}></i>
                  </div>
                  <span className="login-feature-text">{f.text}</span>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Right */}
        <div className="login-right-panel">

          {/* Watermark icons */}
          <i className="fa-solid fa-building-columns login-watermark" style={{ fontSize: '120px', top: '-20px', right: '-20px', transform: 'rotate(15deg)' }}></i>
          <i className="fa-solid fa-file-invoice-dollar login-watermark" style={{ fontSize: '90px', bottom: '30px', left: '-15px', transform: 'rotate(-20deg)' }}></i>
          <i className="fa-solid fa-shield-halved login-watermark" style={{ fontSize: '70px', top: '40%', right: '20px', transform: 'rotate(10deg)' }}></i>
          <i className="fa-solid fa-chart-line login-watermark" style={{ fontSize: '60px', top: '20px', left: '20px', transform: 'rotate(-10deg)' }}></i>
          <i className="fa-solid fa-scale-balanced login-watermark" style={{ fontSize: '55px', bottom: '80px', right: '40px', transform: 'rotate(20deg)' }}></i>

          <div className="login-card">
            <div className="login-card-header">
              <h2 className="login-card-title">
                {step === 'login' ? 'Welcome back' : step === 'forgot_email' ? 'Forgot Password' : 'Reset Password'}
              </h2>
              <div className="login-title-bar"></div>
              <p className="login-card-subtitle">
                {step === 'login' ? 'Sign in to your admin account to continue' : step === 'forgot_email' ? 'Enter your email to receive a reset code' : 'Enter the OTP and your new password'}
              </p>
            </div>

            {error && (
              <div className="login-error-banner" role="alert">
                <i className="fa-solid fa-circle-exclamation"></i>
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="login-error-banner" role="alert" style={{ background: '#f0fdf4', borderColor: '#86efac', color: '#15803d' }}>
                <i className="fa-solid fa-circle-check"></i>
                <span>{success}</span>
              </div>
            )}

            {step === 'login' && (
              <form onSubmit={handleLogin}>
                <div className="admin-form-group">
                <label className="admin-form-label">Email Address</label>
                <div className="admin-input-wrap">
                  <i className="fa-solid fa-envelope admin-input-icon"></i>
                  <input
                    type="email"
                    className="admin-form-input"
                    placeholder="admin@nexustax.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    id="admin-email"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Password</label>
                <div className="admin-input-wrap">
                  <i className="fa-solid fa-lock admin-input-icon"></i>
                  <input
                    type={showPass ? 'text' : 'password'}
                    className="admin-form-input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    id="admin-password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="admin-pass-toggle"
                    onClick={() => setShowPass(!showPass)}
                    tabIndex={-1}
                  >
                    <i className={`fa-solid ${showPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <div className="admin-form-row">
                <label className="admin-remember">
                  <input type="checkbox" id="admin-remember" />
                  <span className="admin-remember-label">Remember me</span>
                </label>
                <button type="button" onClick={() => { setStep('forgot_email'); setError(''); setSuccess(''); }} className="admin-forgot" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="admin-login-btn" disabled={loading} id="admin-login-submit">
                {loading ? (
                  <>
                    <span className="btn-spinner"></span>
                    Signing in…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-right-to-bracket"></i>
                    Sign In
                  </>
                )}
              </button>
            </form>
            )}

            {step === 'forgot_email' && (
              <form onSubmit={handleForgotEmailSubmit}>
                <div className="admin-form-group">
                  <label className="admin-form-label">Email Address</label>
                  <div className="admin-input-wrap">
                    <i className="fa-solid fa-envelope admin-input-icon"></i>
                    <input
                      type="email"
                      className="admin-form-input"
                      placeholder="admin@nexustax.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <button type="submit" className="admin-login-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="btn-spinner"></span>
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane"></i>
                      Send Reset OTP
                    </>
                  )}
                </button>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button type="button" onClick={() => setStep('login')} className="admin-forgot" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    ← Back to Login
                  </button>
                </div>
              </form>
            )}

            {step === 'forgot_otp' && (
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="admin-form-group">
                  <label className="admin-form-label">One-Time Password (OTP)</label>
                  <div className="admin-input-wrap">
                    <i className="fa-solid fa-key admin-input-icon"></i>
                    <input
                      type="text"
                      maxLength="6"
                      className="admin-form-input"
                      placeholder="Enter 6-digit OTP"
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, ''))}
                      required
                      style={{ letterSpacing: '4px' }}
                    />
                  </div>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">New Password</label>
                  <div className="admin-input-wrap">
                    <i className="fa-solid fa-lock admin-input-icon"></i>
                    <input
                      type={showNewPass ? 'text' : 'password'}
                      className="admin-form-input"
                      placeholder="Min. 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="admin-pass-toggle"
                      onClick={() => setShowNewPass(!showNewPass)}
                      tabIndex={-1}
                    >
                      <i className={`fa-solid ${showNewPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Confirm New Password</label>
                  <div className="admin-input-wrap">
                    <i className="fa-solid fa-lock-open admin-input-icon"></i>
                    <input
                      type={showNewPass ? 'text' : 'password'}
                      className="admin-form-input"
                      placeholder="Re-enter password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <button type="submit" className="admin-login-btn" disabled={loading || forgotOtp.length !== 6}>
                  {loading ? (
                    <>
                      <span className="btn-spinner"></span>
                      Resetting…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check-circle"></i>
                      Reset Password
                    </>
                  )}
                </button>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button type="button" onClick={() => setStep('login')} className="admin-forgot" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    Cancel & Back to Login
                  </button>
                </div>
              </form>
            )}

            <div className="login-divider">
              <div className="login-divider-line"></div>
              <span className="login-divider-text">Protected by Legal Infos Security</span>
              <div className="login-divider-line"></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
